import { gql } from "@apollo/client";
import { PriceableItemOrigin } from "elephant-web/gql";
import { useMemo, useState } from "react";
import {
  type ActiveDiseasesQueryResult,
  useActiveDiseasesLazyQuery,
} from "./__generated__/use-search-procedure-lazy-query.gql";
import { sortFilterAndPaginate } from "./helpers";
import type {
  SearchNewItemData,
  SearchNewItemHook,
  SearchNewItemHookResult,
  SearchNewItemParams,
} from "./types";

gql`
  query ActiveDiseases {
    diseases {
      id
      items {
        uuid
        category
        code
        coding
        categoryDisplayName
        displayName
        keywords
      }
      categoryItems {
        displayName
        value
      }
    }
  }
`;

const PROCEDURE_PREFIX_LEN = "Procedure: ".length;
const IS_DISEASE_PROCEDURE_REGEX = /^\$P\d+/;

export function isDefined<T>(foo: T | null | undefined): foo is T {
  return foo !== undefined && foo !== null;
}

function mapGqlResponseToResult(
  { data, loading, error }: ActiveDiseasesQueryResult,
  params: SearchNewItemParams,
): SearchNewItemData {
  const procedures =
    data?.diseases?.items
      ?.filter(isDefined)
      ?.filter((e) => IS_DISEASE_PROCEDURE_REGEX.test(e?.code ?? ""))
      .map((d) => ({
        id: d.uuid,
        name: d?.displayName.substring(PROCEDURE_PREFIX_LEN), // remove 'Procedure: '
        origin: PriceableItemOrigin.Procedure,
      })) ?? [];

  const rows = sortFilterAndPaginate(procedures, params);
  return {
    loading,
    error,
    data: {
      rows,
      totalRows: rows.length,
    },
  };
}

export const useSearchProcedureItemLazyQuery: SearchNewItemHook = () => {
  const [data, setData] = useState<SearchNewItemData>({
    loading: false,
    data: { rows: [], totalRows: 0 },
  });
  const [query] = useActiveDiseasesLazyQuery();

  const wrappedQuery: SearchNewItemHookResult[0] = useMemo(
    () => (args) =>
      query().then((res) => {
        const newData = mapGqlResponseToResult(res, args);
        setData(newData);
        return newData;
      }),
    [query],
  );

  return [wrappedQuery, data];
};

export default useSearchProcedureItemLazyQuery;
